import { addDays, addHours, startOfDay, startOfHour } from 'date-fns';
import { normalizeToLocalDate, parseDate } from './dateOperations';
import { isISO8601DateOnly, relativeDateToISOString } from './dateTimeUtils';
import { dissectRelativeDate } from './dissectRelativeDate';
import { TimeFrameUnit } from './TimeFrameUnit';

type DateRangeBounds = {
  start: string;
  end: string;
};

type BoundWindow = {
  from: Date;
  toExcluding: Date;
};

const toHourWindow = (date: Date): BoundWindow => {
  const from = startOfHour(date);
  return { from, toExcluding: addHours(from, 1) };
};

const toDayWindow = (date: Date): BoundWindow => {
  const from = startOfDay(date);
  return { from, toExcluding: addDays(from, 1) };
};

const toBoundWindow = (bound: string, now: Date): BoundWindow | null => {
  const dissected = dissectRelativeDate(bound);
  if (dissected) {
    const date = parseDate(relativeDateToISOString(bound, now));
    const isHourPrecision =
      dissected.unit === TimeFrameUnit.HOUR_AGO ||
      dissected.unit === TimeFrameUnit.HOUR_FROM_NOW;
    return isHourPrecision ? toHourWindow(date) : toDayWindow(date);
  }
  if (isISO8601DateOnly(bound)) {
    return toDayWindow(parseDate(normalizeToLocalDate(bound)));
  }
  return null;
};

/**
 * Whether a range's bounds are the wrong way round, so it can match nothing.
 * Like the API, each bound stands for the whole day it falls on, or the whole
 * hour for a relative date in hours, and both ends are inclusive. A bound
 * that is empty or not a date never makes a range reversed.
 */
export const isDateRangeReversed = (
  { start, end }: DateRangeBounds,
  now: Date = new Date()
): boolean => {
  const startWindow = toBoundWindow(start, now);
  const endWindow = toBoundWindow(end, now);
  if (!startWindow || !endWindow) return false;

  return startWindow.from >= endWindow.toExcluding;
};
